import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import type {
  Notification,
  NotificationPreferences,
} from '@shared/models/notification';

@Injectable({ providedIn: 'root' })
export class NotificationApi {
  readonly #http = inject(HttpClient);
  readonly #baseUrl = `${environment.apiUrl}/notifications`;

  list() {
    return this.#http.get<{ notifications: Notification[] }>(this.#baseUrl);
  }

  getUnreadCount() {
    return this.#http.get<{ count: number }>(`${this.#baseUrl}/unread-count`);
  }

  markRead(id: string) {
    return this.#http.patch<{ success: boolean }>(
      `${this.#baseUrl}/${id}/read`,
      {},
    );
  }

  markAllRead() {
    return this.#http.patch<{ success: boolean }>(
      `${this.#baseUrl}/read-all`,
      {},
    );
  }

  getPreferences() {
    return this.#http.get<{ preferences: NotificationPreferences }>(
      `${this.#baseUrl}/preferences`,
    );
  }

  updatePreferences(preferences: Partial<NotificationPreferences>) {
    return this.#http.put<{ preferences: NotificationPreferences }>(
      `${this.#baseUrl}/preferences`,
      { preferences },
    );
  }

  getVapidPublicKey() {
    return this.#http.get<{ publicKey: string }>(
      `${this.#baseUrl}/vapid-public-key`,
    );
  }

  savePushSubscription(
    sub:
      | {
          type?: 'web';
          deviceId?: string;
          endpoint: string;
          keys: { p256dh: string; auth: string };
        }
      | {
          type: 'native';
          deviceId: string;
          fcmToken: string;
          platform: 'ios' | 'android';
        },
  ) {
    return this.#http.post<{ success: boolean }>(
      `${this.#baseUrl}/push-subscription`,
      sub,
    );
  }

  deletePushSubscription(
    identifier: { deviceId: string } | { endpoint: string },
  ) {
    return this.#http.delete<{ success: boolean }>(
      `${this.#baseUrl}/push-subscription`,
      { body: identifier },
    );
  }

  checkPushSubscription(
    identifier: { deviceId: string } | { endpoint: string },
  ) {
    const params: Record<string, string> =
      'deviceId' in identifier
        ? { deviceId: identifier.deviceId }
        : { endpoint: identifier.endpoint };
    return this.#http.get<{ subscribed: boolean }>(
      `${this.#baseUrl}/push-subscription`,
      { params },
    );
  }
}
